import React, { useState, useEffect, useRef } from 'react';
import { createThread, createMessage, createRun, titleToAssistantIDMap } from '../chat/openAIUtils';
import SlideContent from './SlideContent';
import './FiveDThinking.css';
import FeedbackModal from './FeedbackModal/FeedbackModal';
import LoginModal from '../login/LoginModal'; // Assuming you have a LoginModal component
import { readFileContent } from '../utils/fileUtils';
import {
    FaBook,
    FaChevronDown,
    FaChevronUp,
    FaPaperclip,
    FaExclamationCircle,
    FaGraduationCap,
    FaListAlt,
    FaDownload,
    FaFilePowerpoint,
    FaGoogleDrive,
    FaFileWord,
    FaSpinner,
    FaInfoCircle,
    FaComments,
    FaSync
  } from 'react-icons/fa';
import fiveDThinkingImg from '../img/5dlogotransparent.png';

import { auth, db } from '../config/firebase-config'; // Adjust path based on your setup
import { doc, getDoc, updateDoc, arrayUnion, increment, setDoc } from 'firebase/firestore';
import { useAuthState } from 'react-firebase-hooks/auth';


import exportSlidesAsPptx from './Export/ExportAsPPTX.jsx';
import exportAsGoogleDoc from './Export/ExportAsGoogleDoc';
import exportAsWord from './Export/ExportAsWord';
import { createPresentation, updatePresentation, } from './Export/ExportAsGoogleSlides'; // Adjust path as necessary
import BetaToggle from './BetaToggle.jsx';

const dimensionColors = {
  Objectives: '0070C0', // Choose any hex color
  Explore: 'fa6666',
  Compare: '000000',
  Question: '64aae8',
  Connect: 'ffa600',
  Appreciate: '35b8b1',
  Activities: 'ffcccb',
};

const DIMENSIONS_CONFIG_BASE = [
  { name: 'Objectives', title: 'Learning Objectives' },
  { name: 'Explore', title: 'Analytical thinking - Explore' },
  { name: 'Compare', title: 'Analogical thinking - Compare' },
  { name: 'Question', title: 'Critical thinking - Question' },
  { name: 'Connect', title: 'Meditative thinking - Connect' },
  { name: 'Appreciate', title: 'Moral thinking - Appreciate' },
  { name: 'Activities', title: 'Activities' },
];

const FiveDAssistant = () => {
  const [user] = useAuthState(auth);

  const [selectedDimension, setSelectedDimension] = useState('Explore');
  const [isExportDropdownOpen, setIsExportDropdownOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const toggleExportDropdown = () => {
    console.log('Toggle export dropdown clicked');
    setIsExportDropdownOpen((prev) => !prev);
  };
  
  const handleExportToGoogleDocs = async () => {
    if (slides.length === 0) {
      alert('No slides available to export.');
      return;
    }

    console.log('DEBUG: Exporting slides to Google Docs:', slides);
    try {
      await exportAsGoogleDoc(slides);
      alert('Google Docs export successful!');
    } catch (error) {
      console.error('Error exporting to Google Docs:', error);
      alert('Failed to export to Google Docs. Please try again.');
    } finally {
      setIsExportDropdownOpen(false);
    }
  };
  
  const handleExportToWord = async () => {
    if (slides.length === 0) {
      alert('No slides available to export.');
      return;
    }

    console.log('DEBUG: Exporting slides to Word:', slides);
    try {
      await exportAsWord(slides);
      alert('Word export successful!');
    } catch (error) {
      console.error('Error exporting to Word:', error);
      alert('Failed to export to Word. Please try again.');
    } finally {
      setIsExportDropdownOpen(false);
    }
  };

  // State variables for required and optional inputs
  const [topic, setTopic] = useState(''); // Required Topic input
  const [gradeLevel, setGradeLevel] = useState(''); // Optional Grade Level input
  
  // State variables for Topic file upload
  const [topicFile, setTopicFile] = useState(null);
  const [topicPreview, setTopicPreview] = useState('');

  // State variables for each dimension input (Optional)

  // State variables for Objectives dimension input
  const [objectivesInput, setObjectivesInput] = useState('');
  const [objectivesFile, setObjectivesFile] = useState(null);

  const [activitiesInput, setActivitiesInput] = useState('');
  const [activitiesFile, setActivitiesFile] = useState(null);

  const [exploreInput, setExploreInput] = useState('');
  const [exploreFile, setExploreFile] = useState(null);

  const [compareInput, setCompareInput] = useState('');
  const [compareFile, setCompareFile] = useState(null);

  const [questionInput, setQuestionInput] = useState('');
  const [questionFile, setQuestionFile] = useState(null);

  const [connectInput, setConnectInput] = useState('');
  const [connectFile, setConnectFile] = useState(null);

  const [appreciateInput, setAppreciateInput] = useState('');
  const [appreciateFile, setAppreciateFile] = useState(null);

  // Existing state variables
  const [isLoading, setIsLoading] = useState(false);
  const [slides, setSlides] = useState([]);
  const [responseBuffer, setResponseBuffer] = useState('');
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [error, setError] = useState('');
  const [presentationLink, setPresentationLink] = useState('');
  const [currentThreadId, setCurrentThreadId] = useState(null);

  const [standards, setStandards] = useState('');
  const [standardsFile, setStandardsFile] = useState(null);
  const [standardsPreview, setStandardsPreview] = useState('');

  const [isBetaTest, setIsBetaTest] = useState(false);
  const toggleBetaTest = () => setIsBetaTest((prev) => !prev);

  // State to control the visibility of the dropdown
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Reference for the dropdown to handle clicks outside
  const dropdownRef = useRef(null);

  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [collectedResponses, setCollectedResponses] = useState({});
  const [assistantModel, setAssistantModel] = useState('');
  const [currentlyResettingKey, setCurrentlyResettingKey] = useState(null); // Added for spinner
   
  // Moved function definitions before their first potential use in handleMessage or other dependent functions
  const createSlidesFromResponse = (response, dimensionName) => {
    console.log('dimensionName:', dimensionName, 'response:', response);
    const slides = [];
    let subtopicIndexForId = 0; // Keep this for ensuring unique IDs if needed beyond contentKey

    const sanitizeContentKeyForId = (key) => key.replace(/[^a-zA-Z0-9_]/g, '_');

    const addSlideIfContentExists = (contentKey, slideSpecificData, explicitSubtopicIndex) => {
      const content = response[contentKey];
      if (
        content &&
        (Array.isArray(content) ? content.length > 0 : typeof content === 'object' || (typeof content === 'string' && content.trim()))
      ) {
        const sanitizedKey = sanitizeContentKeyForId(contentKey);
        const subIdx = explicitSubtopicIndex !== undefined ? explicitSubtopicIndex : slides.filter(s => s.dimension === dimensionName).length;
        const id = `${dimensionName}-${sanitizedKey}-${subIdx}`;
        slides.push({ 
            id, 
            dimension: dimensionName, 
            contentKey: contentKey, 
            subtopicIndex: subIdx, 
            ...slideSpecificData 
        });
      }
    };
  
  if (dimensionName === 'Objectives') {
    if (response.learningObjectives) {
      const id = `${dimensionName}-learningObjectives-0`;
      slides.push({ id, dimension: dimensionName, contentKey: 'learningObjectives', learningObjectives: response.learningObjectives, subtopicIndex: 0 });
    }
  }  else if (dimensionName === 'Explore') {
      addSlideIfContentExists('content', { content: response.content });
      addSlideIfContentExists('explanation', { explanation: response.explanation });
      addSlideIfContentExists('observations', { observations: response.observations });
      addSlideIfContentExists('fascinatingFacts', { fascinatingFacts: response.fascinatingFacts });

    } else if (dimensionName === 'Compare') {
      addSlideIfContentExists('analogy', { analogy: response.analogy });
      addSlideIfContentExists('content', { content: response.content });
      addSlideIfContentExists('explanation', { explanation: response.explanation });
      addSlideIfContentExists('comparison', { comparison: response.comparison });

    }  else if (dimensionName === 'Question') {
      const questionSubtopicKeys = [
        'negation_of_chance',
        'negation_of_material_causes',
        'negation_of_nature',
        'conclusion'
      ];
      
      questionSubtopicKeys.forEach((key, index) => {
        const sanitizedKey = sanitizeContentKeyForId(key);
        const id = `${dimensionName}-${sanitizedKey}-${index}`;
        if (key === 'conclusion' && response.conclusion && response.conclusion.trim()) {
          slides.push({
            id,
            dimension: dimensionName,
            subtopicIndex: index,
            contentKey: key,
            conclusion: response.conclusion,
          });
        } else if (response.questions && response.questions[key] && response.questions[key].length > 0) {
          slides.push({
            id,
            dimension: dimensionName,
            subtopicIndex: index,
            contentKey: key,
            questions: response.questions[key],
          });
        }
      });
    }  else if (dimensionName === 'Connect') {
      const c = response.connections || {};
      let currentSubtopicIdx = 0;

      
      if (c.interconnectedness && c.interconnectedness.trim()) {
        const id = `${dimensionName}-interconnectedness-${currentSubtopicIdx}`;
        slides.push({
          id, dimension: 'Connect', subtopicIndex: currentSubtopicIdx, contentKey: 'interconnectedness',
          interconnectedness: c.interconnectedness,
        });
        currentSubtopicIdx++;
      }
      if (c.completeDependency && c.completeDependency.trim()) {
        const id = `${dimensionName}-completeDependency-${currentSubtopicIdx}`;
        slides.push({
          id, dimension: 'Connect', subtopicIndex: currentSubtopicIdx, contentKey: 'completeDependency',
          completeDependency: c.completeDependency, 
        });
        currentSubtopicIdx++;
      }
      if (response.allahNames && response.allahNames.namesInEnglish && response.allahNames.namesInEnglish.length > 0) {
         const id = `${dimensionName}-allahNames-${currentSubtopicIdx}`;
        slides.push({
          id, dimension: 'Connect', subtopicIndex: currentSubtopicIdx, contentKey: 'allahNames',
          allahNames: response.allahNames,
        });
        currentSubtopicIdx++;
      }
      ['analogicalReflection', 'questionsForDeeperConnection', 'contemplationAndAppreciation'].forEach(key => {
        if (response[key] && (typeof response[key] === 'string' ? response[key].trim() : true)) {
          addSlideIfContentExists(key, { [key]: response[key] }, currentSubtopicIdx);
          if(response[key]) currentSubtopicIdx++; 
        }
      });

    } else if (dimensionName === 'Appreciate') {
      addSlideIfContentExists('whatIfs', { whatIfs: response.whatIfs });
      if (response.zikrFikrShukr && Object.keys(response.zikrFikrShukr).length > 0 && 
          (response.zikrFikrShukr.zikr?.length || response.zikrFikrShukr.fikr?.length || response.zikrFikrShukr.shukr?.length)) {
        const id = `${dimensionName}-zikrFikrShukr-${slides.filter(s => s.dimension === dimensionName).length}`;
        slides.push({
          id, dimension: 'Appreciate', contentKey: 'zikrFikrShukr',
          zikrFikrShukr: response.zikrFikrShukr,
          subtopicIndex: slides.filter(s => s.dimension === dimensionName).length 
        });
      }
      addSlideIfContentExists('characterLessons', { characterLessons: response.characterLessons });
      addSlideIfContentExists('connectWithQuran', { connectWithQuran: response.connectWithQuran });
      addSlideIfContentExists('connectWithHadith', { connectWithHadith: response.connectWithHadith });
      addSlideIfContentExists('dua', { dua: response.dua });

    } else if (dimensionName === 'Activities') {
      const activitiesData = response.activities || [];
      if (activitiesData.length > 0) {
        const id = `${dimensionName}-main-0`;
        slides.push({
          id, dimension: 'Activities', contentKey: 'main',
          activities: activitiesData.map(activity => ({
            title: activity.title, dimension: activity.dimension, objective: activity.objective,
            materials: activity.materials || [], instructions: activity.instructions || [],
          })),
          subtopicIndex: 0 
        });
      }
    }
  
    console.log('Generated slides with IDs:', slides);
    return slides;
  };

  const createSlidesFromResponseForReset = (partialResponse, originalSlide) => {
    console.log("createSlidesFromResponseForReset called with partialResponse:", partialResponse, "originalSlide:", originalSlide);
    const { id, dimension: dimensionName, contentKey, subtopicIndex } = originalSlide;
    let updatedSlideDataOnly = {}; 

    if (dimensionName === 'Objectives' && partialResponse.learningObjectives) {
        updatedSlideDataOnly = { learningObjectives: partialResponse.learningObjectives };
    } else if (dimensionName === 'Explore') {
        // Handle all Explore content types
        if (contentKey === 'content' && partialResponse.content !== undefined) {
            updatedSlideDataOnly = { content: partialResponse.content };
        } else if (contentKey === 'explanation' && partialResponse.explanation !== undefined) {
            updatedSlideDataOnly = { explanation: partialResponse.explanation };
        } else if (contentKey === 'observations' && partialResponse.observations !== undefined) {
            updatedSlideDataOnly = { observations: partialResponse.observations };
        } else if (contentKey === 'fascinatingFacts' && partialResponse.fascinatingFacts !== undefined) {
            updatedSlideDataOnly = { fascinatingFacts: partialResponse.fascinatingFacts };
        } else if (partialResponse[contentKey] !== undefined) {
            updatedSlideDataOnly = { [contentKey]: partialResponse[contentKey] };
        }
    } else if (dimensionName === 'Compare') {
        // Handle all Compare content types
        if (contentKey === 'analogy' && partialResponse.analogy !== undefined) {
            updatedSlideDataOnly = { analogy: partialResponse.analogy };
        } else if (contentKey === 'content' && partialResponse.content !== undefined) {
            updatedSlideDataOnly = { content: partialResponse.content };
        } else if (contentKey === 'explanation' && partialResponse.explanation !== undefined) {
            updatedSlideDataOnly = { explanation: partialResponse.explanation };
        } else if (contentKey === 'comparison' && partialResponse.comparison !== undefined) {
            updatedSlideDataOnly = { comparison: partialResponse.comparison };
        } else if (partialResponse[contentKey] !== undefined) {
            updatedSlideDataOnly = { [contentKey]: partialResponse[contentKey] };
        }
    } else if (dimensionName === 'Question') {
        if (contentKey === 'conclusion' && partialResponse.conclusion !== undefined) {
            updatedSlideDataOnly = { conclusion: partialResponse.conclusion };
        } else if (partialResponse.questions && partialResponse.questions[contentKey]) {
            updatedSlideDataOnly = { questions: partialResponse.questions[contentKey] };
        } else if (partialResponse.questions !== undefined) { 
            updatedSlideDataOnly = { questions: partialResponse.questions };
        }
    } else if (dimensionName === 'Connect') {
        if (contentKey === 'allahNames' && partialResponse.allahNames) {
            updatedSlideDataOnly = { allahNames: partialResponse.allahNames };
        } else if (contentKey === 'completeDependency' && partialResponse.connections && partialResponse.connections.completeDependency !== undefined) {
            updatedSlideDataOnly = { completeDependency: partialResponse.connections.completeDependency };
        } else if (contentKey === 'interconnectedness' && partialResponse.connections && partialResponse.connections.interconnectedness !== undefined) {
            updatedSlideDataOnly = { interconnectedness: partialResponse.connections.interconnectedness };
        } else if (contentKey === 'analogicalReflection' && partialResponse.analogicalReflection !== undefined) {
            updatedSlideDataOnly = { analogicalReflection: partialResponse.analogicalReflection };
        } else if (contentKey === 'questionsForDeeperConnection' && partialResponse.questionsForDeeperConnection !== undefined) {
            updatedSlideDataOnly = { questionsForDeeperConnection: partialResponse.questionsForDeeperConnection };
        } else if (contentKey === 'contemplationAndAppreciation' && partialResponse.contemplationAndAppreciation !== undefined) {
            updatedSlideDataOnly = { contemplationAndAppreciation: partialResponse.contemplationAndAppreciation };
        } else if (partialResponse[contentKey] !== undefined) { 
            updatedSlideDataOnly = { [contentKey]: partialResponse[contentKey] };
        }
    } else if (dimensionName === 'Appreciate') {
        if (contentKey === 'whatIfs' && partialResponse.whatIfs !== undefined) {
            updatedSlideDataOnly = { whatIfs: partialResponse.whatIfs };
        } else if (contentKey === 'characterLessons' && partialResponse.characterLessons !== undefined) {
            updatedSlideDataOnly = { characterLessons: partialResponse.characterLessons };
        } else if (contentKey === 'dua' && partialResponse.dua !== undefined) {
            updatedSlideDataOnly = { dua: partialResponse.dua };
        } else if (contentKey === 'zikrFikrShukr' && partialResponse.zikrFikrShukr) {
            updatedSlideDataOnly = { zikrFikrShukr: partialResponse.zikrFikrShukr };
        } else if (contentKey === 'connectWithQuran' && Array.isArray(partialResponse)) {
            // Handle case where AI returns the array directly instead of wrapped in an object
            updatedSlideDataOnly = { connectWithQuran: partialResponse };
        } else if (contentKey === 'connectWithQuran' && partialResponse.connectWithQuran) {
            updatedSlideDataOnly = { connectWithQuran: partialResponse.connectWithQuran };
        } else if (contentKey === 'connectWithHadith' && Array.isArray(partialResponse)) {
            // Handle case where AI returns the array directly instead of wrapped in an object
            updatedSlideDataOnly = { connectWithHadith: partialResponse };
        } else if (contentKey === 'connectWithHadith' && partialResponse.connectWithHadith) {
            updatedSlideDataOnly = { connectWithHadith: partialResponse.connectWithHadith };
        } else if (partialResponse[contentKey] !== undefined) { 
             updatedSlideDataOnly = { [contentKey]: partialResponse[contentKey] };
        }
    } else if (dimensionName === 'Activities' && partialResponse.activities) {
        updatedSlideDataOnly = { 
            activities: partialResponse.activities.map(activity => ({
                title: activity.title, dimension: activity.dimension, objective: activity.objective,
                materials: activity.materials || [], instructions: activity.instructions || [],
            }))
        };
    } else {
        console.warn("Unhandled case or unexpected partialResponse structure in createSlidesFromResponseForReset:", dimensionName, contentKey, partialResponse);
        if (typeof partialResponse === 'object' && partialResponse !== null && Object.keys(partialResponse).length > 0) {
            updatedSlideDataOnly = { ...partialResponse }; 
        } else {
            return []; 
        }
    }
    
    if (Object.keys(updatedSlideDataOnly).length === 0) {
        console.error("Failed to extract any updatable data from partialResponse for reset:", partialResponse, "Original Slide:", originalSlide);
        return []; 
    }

    const newContentForSlide = {
        ...updatedSlideDataOnly 
    };
    console.log("Content to merge by createSlidesFromResponseForReset:", newContentForSlide);
    return [newContentForSlide]; 
  };
   
  const handleBetaToggle = (e) => {
    setIsBetaTest(e.target.checked);
  };


  const handleFeedbackSubmit = async (feedbackData) => {
    try {
      await setDoc(doc(db, '5DThinkingLessonPlanFeedback', new Date().toISOString()), feedbackData);
      console.log('Feedback saved successfully:', feedbackData);
    } catch (error) {
      console.error('Error saving feedback:', error);
    }
  };
  

  useEffect(() => {
    // Close the dropdown when clicking outside of it
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  const fetchUserData = async () => {
    if (!user) return;
  
    const userDoc = await getDoc(doc(db, 'users', user.uid));
    if (!userDoc.exists()) {
      // Create the user document if it doesn't exist
      await setDoc(doc(db, 'users', user.uid), {
        usageCount: 0,
        threads: [],
      });
    }
  };

  // Function to handle dimension selection
  const handleDimensionSelect = (dimension) => {
    setSelectedDimension(dimension);
    setIsDropdownOpen(false);
  };

  // Handlers for input changes
  const handleTopicChange = (e) => setTopic(e.target.value);
  const handleGradeLevelChange = (e) => setGradeLevel(e.target.value);

  // Handlers for each dimension input

  const handleObjectivesInputChange = (e) => setObjectivesInput(e.target.value);
  const handleExploreInputChange = (e) => setExploreInput(e.target.value);
  const handleCompareInputChange = (e) => setCompareInput(e.target.value);
  const handleQuestionInputChange = (e) => setQuestionInput(e.target.value);
  const handleConnectInputChange = (e) => setConnectInput(e.target.value);
  const handleAppreciateInputChange = (e) => setAppreciateInput(e.target.value);


  const handleStandardsChange = (e) => setStandards(e.target.value);

  const handleFileChange = (e, setFile, setPreview = null) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['text/plain', 'application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowedTypes.includes(file.type) && !file.name.toLowerCase().endsWith('.txt') && !file.name.toLowerCase().endsWith('.doc') && !file.name.toLowerCase().endsWith('.docx') && !file.name.toLowerCase().endsWith('.pdf')) {
        handleError(new Error('Invalid file type. Please upload a .txt, .doc, .docx, or .pdf file.'), 'fileReading');
        return;
      }

      // Validate file size (10MB limit)
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        handleError(new Error('File too large. Please upload a file smaller than 10MB.'), 'fileReading');
        return;
      }

      setFile(file);
      if (setPreview) {
        readFileContent(file)
          .then((content) => {
            setPreview(content);
          })
          .catch((error) => {
            handleError(new Error(`Failed to read file: ${error.message}`), 'fileReading');
            setPreview('');
            setFile(null);
          });
      }
    }
  };
  
  const handleFileDrop = (e, setFile, setPreview = null) => {
    e.preventDefault();
    
    try {
      const file = e.dataTransfer.files[0];
      if (file) {
        // Validate file type
        const allowedTypes = ['text/plain', 'application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
        if (!allowedTypes.includes(file.type) && !file.name.toLowerCase().endsWith('.txt') && !file.name.toLowerCase().endsWith('.doc') && !file.name.toLowerCase().endsWith('.docx') && !file.name.toLowerCase().endsWith('.pdf')) {
          handleError(new Error('Invalid file type. Please upload a .txt, .doc, .docx, or .pdf file.'), 'fileReading');
          return;
        }

        // Validate file size (10MB limit)
        const maxSize = 10 * 1024 * 1024; // 10MB
        if (file.size > maxSize) {
          handleError(new Error('File too large. Please upload a file smaller than 10MB.'), 'fileReading');
          return;
        }

        setFile(file);
        if (setPreview) {
          readFileContent(file)
            .then((content) => {
              setPreview(content);
            })
            .catch((error) => {
              handleError(new Error(`Failed to read dropped file: ${error.message}`), 'fileReading');
              setPreview('');
              setFile(null);
            });
        }
      }
    } catch (error) {
      handleError(new Error('Failed to process dropped file.'), 'fileReading');
    }
  };

  const handleError = (error, context = 'general') => {
    console.error('Error occurred:', error, 'Context:', context);
    
    let userMessage = '';
    
    switch (context) {
      case 'network':
        userMessage = 'Network connection issue. Please check your internet connection and try again.';
        break;
      case 'authentication':
        userMessage = 'Authentication failed. Please log in again and retry.';
        break;
      case 'fileReading':
        userMessage = 'Unable to read the uploaded file. Please ensure the file is not corrupted and try uploading again.';
        break;
      case 'aiProcessing':
        userMessage = 'AI processing failed. The service might be temporarily unavailable. Please try again in a few moments.';
        break;
      case 'slideGeneration':
        userMessage = 'Failed to generate slides. Please check your inputs and try again.';
        break;
      case 'reset':
        userMessage = 'Failed to reset the section. Please try again or regenerate the entire lesson plan.';
        break;
      case 'export':
        userMessage = 'Export failed. Please ensure you\'re logged in with proper permissions and try again.';
        break;
      case 'invalidInput':
        userMessage = 'Invalid input detected. Please check your topic content and try again.';
        break;
      case 'rateLimited':
        userMessage = 'Too many requests. Please wait a moment before trying again.';
        break;
      default:
        if (error?.message) {
          // Check for specific error types in the message
          if (error.message.includes('network') || error.message.includes('fetch')) {
            userMessage = 'Network error occurred. Please check your connection and try again.';
          } else if (error.message.includes('unauthorized') || error.message.includes('403')) {
            userMessage = 'Access denied. Please log in again and try again.';
          } else if (error.message.includes('quota') || error.message.includes('limit')) {
            userMessage = 'Service limit reached. Please try again later.';
          } else if (error.message.includes('timeout')) {
            userMessage = 'Request timed out. Please try again.';
          } else {
            userMessage = `An error occurred: ${error.message}. Please try again.`;
          }
        } else {
          userMessage = 'An unexpected error occurred. Please try again.';
        }
    }
    
    setError(userMessage);
    setIsLoading(false);
    setCurrentlyResettingKey(null);
    setIsExporting(false);
  };

  const handleSubmit = async () => {
    try {
      // Get topicContent
      let topicContent = topic;
      if (topicFile) {
        try {
          topicContent = await readFileContent(topicFile);
        } catch (fileError) {
          handleError(fileError, 'fileReading');
          return;
        }
      }

      if (!topicContent.trim()) {
        handleError(new Error('Topic content is required'), 'invalidInput');
        return;
      }

      // Get standardsContent
      let standardsContent = standards;
      if (standardsFile) {
        try {
          standardsContent = await readFileContent(standardsFile);
        } catch (fileError) {
          handleError(fileError, 'fileReading');
          return;
        }
      }

      // Prepare dimension inputs
      const dimensionInputs = {};
      const dimensionsList = ['Objectives', 'Explore', 'Compare', 'Question', 'Connect', 'Appreciate', 'Activities'];
      
      try {
        for (const dimension of dimensionsList) {
          let inputText = '';
          let inputFile = null;

          switch (dimension) {
            case 'Activities':
              inputText = activitiesInput;
              inputFile = activitiesFile;
              break;
            case 'Objectives':
              inputText = objectivesInput;
              inputFile = objectivesFile;
              break;
            case 'Explore':
              inputText = exploreInput;
              inputFile = exploreFile;
              break;
            case 'Compare':
              inputText = compareInput;
              inputFile = compareFile;
              break;
            case 'Question':
              inputText = questionInput;
              inputFile = questionFile;
              break;
            case 'Connect':
              inputText = connectInput;
              inputFile = connectFile;
              break;
            case 'Appreciate':
              inputText = appreciateInput;
              inputFile = appreciateFile;
              break;
            default:
              break;
          }

          let inputContent = inputText;
          if (inputFile) {
            try {
              const fileContent = await readFileContent(inputFile);
              inputContent += '\n' + fileContent;
            } catch (fileError) {
              handleError(new Error(`Failed to read ${dimension} file`), 'fileReading');
              return;
            }
          }

          dimensionInputs[dimension] = inputContent;
        }
      } catch (processingError) {
        handleError(processingError, 'slideGeneration');
        return;
      }

      setIsLoading(true);
      setError('');
      setSlides([]);

      const processedDimensions = DIMENSIONS_CONFIG_BASE.map(dimConfig => ({
        ...dimConfig,
        input: dimensionInputs[dimConfig.name] || 'N/A'
      }));

      try {
        const assistantTitle = isBetaTest ? '5D Thinking-2' : '5D Thinking-1';
        setAssistantModel(assistantTitle);
        
        const thread = await createThread(assistantTitle);
        setCurrentThreadId(thread.id);
        console.log("Thread created successfully:", thread);
        
        // Store the thread and update usage count in Firebase
        if (user) {
          try {
            const userRef = doc(db, 'users', user.uid);
            await updateDoc(userRef, {
              '5DThinking.threads': arrayUnion(thread.id),
              '5DThinking.usageCount': increment(1),
            });
          } catch (dbError) {
            console.warn('Failed to update user data in database:', dbError);
            // Don't stop the process for database errors
          }
        }
        
        for (const dimension of processedDimensions) {
          await fetchContentForDimension(
            thread.id,
            dimension,
            topicContent,
            gradeLevel,
            standardsContent,
            assistantTitle,
            false
          );
        }
      } catch (aiError) {
        if (aiError.message?.includes('quota') || aiError.message?.includes('limit')) {
          handleError(aiError, 'rateLimited');
        } else if (aiError.message?.includes('network') || aiError.message?.includes('fetch')) {
          handleError(aiError, 'network');
        } else if (aiError.message?.includes('unauthorized')) {
          handleError(aiError, 'authentication');
        } else {
          handleError(aiError, 'aiProcessing');
        }
      }
    } catch (error) {
      handleError(error, 'slideGeneration');
    }
  };

  const fetchContentForDimension = async (
    threadId,
    dimension,
    topicContent,
    gradeLevel,
    standardsContent,
    assistantTitle,
    isResetting = false,
    slideIdBeingReset = null, // New: to know which specific slide is being reset
    customPrompt = null // New: to use a custom prompt for reset
  ) => {
    const dimensionInput = dimension.input || 'N/A';
    // Use customPrompt if provided (for resets), otherwise use the standard prompt
    const prompt = customPrompt || `
Topic: ${topicContent}
Grade Level: ${gradeLevel || 'N/A'}
Standards: ${standardsContent || 'N/A'}

Dimension: ${dimension.name}
Dimension Input: ${dimensionInput}

Focus only on creating a comprehensive ${dimension.title} thinking response.
`;
    console.log(`Fetching content. Dimension: ${dimension.name}${isResetting ? `, Resetting SlideID: ${slideIdBeingReset}` : ''}. Using prompt:`, prompt);
    
    return new Promise((resolve, reject) => {
      createMessage(threadId, prompt, assistantTitle)
        .then(() => {
          createRun(
            threadId,
            titleToAssistantIDMap[assistantTitle],
            // Pass slideIdBeingReset to handleMessage
            (message) => handleMessage(message, dimension.name, resolve, isResetting, slideIdBeingReset),
            handleError,
            assistantTitle
          );
        })
        .catch((error) => {
          handleError(error);
          reject(error);
        });
    });
  };
  const handleMessage = (() => {
    const messageBuffer = {}; 
  
    return (message, dimensionName, resolve, isResetting = false, slideIdBeingReset = null) => {
      try {
        // Use dimensionName for buffer key during initial load, slideIdBeingReset if available during reset
        const bufferKey = isResetting && slideIdBeingReset ? slideIdBeingReset : dimensionName;

        if (!messageBuffer[bufferKey]) {
          messageBuffer[bufferKey] = '';
        }
  
        if (message.text === 'END_TOKEN') {
          console.log(`Final accumulated response for ${bufferKey}:`, messageBuffer[bufferKey]);
          try {
            const response = JSON.parse(messageBuffer[bufferKey]);
            
            if (isResetting && slideIdBeingReset) {
              // For reset, createSlidesFromResponse needs to parse the targeted AI response
              // and return a single, updated slide object.
              const originalSlide = slides.find(s => s.id === slideIdBeingReset);
              if (!originalSlide) {
                  console.error("Original slide not found for reset:", slideIdBeingReset);
                  handleError(new Error(`Error processing reset for ${slideIdBeingReset}. Original slide not found.`), 'reset');
                  resolve();
                  return;
              } else {
                  // This function will now need to be adapted to handle partial AI responses for a reset
                  const newSingleSlideArray = createSlidesFromResponseForReset(response, originalSlide);
                  if (newSingleSlideArray && newSingleSlideArray.length > 0) {
                      const newSlide = newSingleSlideArray[0];
                      console.log('Updating slide with reset content:', { originalSlide, newSlide });
                      setSlides(prevSlides => {
                        const updatedSlides = prevSlides.map(s => s.id === slideIdBeingReset ? { 
                          ...s, 
                          ...newSlide, 
                          id: s.id,
                          dimension: s.dimension,
                          contentKey: s.contentKey,
                          subtopicIndex: s.subtopicIndex
                        } : s);
                        
                        // Debug: Log the final updated slide
                        const finalUpdatedSlide = updatedSlides.find(s => s.id === slideIdBeingReset);
                        console.log('Final updated slide after reset:', finalUpdatedSlide);
                        
                        return updatedSlides;
                      });
                  } else {
                       console.error("createSlidesFromResponseForReset did not return a valid slide for ID:", slideIdBeingReset, "AI Response:", response);
                       handleError(new Error(`Failed to update slide ${slideIdBeingReset} with new content.`), 'reset');
                       resolve();
                       return;
                  }
              }
            } else {
              // Initial generation: creates all slides for the dimension
              const newSlidesForDimension = createSlidesFromResponse(response, dimensionName);
              setSlides((prevSlides) => {
                // Remove any existing slides for this dimension first (e.g., if re-generating)
                // This logic might need adjustment if we only append. 
                // For now, assume a full replacement for the dimension on initial gen.
                const slidesOfOtherDimensions = prevSlides.filter(s => s.dimension !== dimensionName);
                const resultSlides = [];
                const dimensionsOrder = DIMENSIONS_CONFIG_BASE.map(d => d.name);

                for (const orderedDimName of dimensionsOrder) {
                  if (orderedDimName === dimensionName) {
                    resultSlides.push(...newSlidesForDimension);
                  } else {
                    // Check if slides for this other dimension already exist
                    const existingOtherDimSlides = slidesOfOtherDimensions.filter(s => s.dimension === orderedDimName);
                    if (existingOtherDimSlides.length > 0) {
                        resultSlides.push(...existingOtherDimSlides);
                    } else {
                        // If generating sequentially and this other dimension hasn't been processed yet,
                        // they will be added when its turn comes.
                    }
                  }
                }
                // Ensure all newly generated slides are present
                if (!resultSlides.some(s => s.dimension === dimensionName) && newSlidesForDimension.length > 0) {
                    // This case handles if the dimension was not in the order for some reason or first time
                    // It should correctly place them based on dimensionsOrder or append if something is off
                    let inserted = false;
                    const finalSlides = [];
                    for(const orderedDimName of dimensionsOrder) {
                        if(orderedDimName === dimensionName) {
                            finalSlides.push(...newSlidesForDimension);
                            inserted = true;
                        } else {
                            finalSlides.push(...prevSlides.filter(s => s.dimension === orderedDimName));
                        }
                    }
                    if(!inserted) finalSlides.push(...newSlidesForDimension); // Fallback append

                    return finalSlides.filter((slide, index, self) => 
                      index === self.findIndex((t) => (t.id === slide.id))
                    ); // Ensure unique IDs
                }
                return resultSlides.filter((slide, index, self) => 
                  index === self.findIndex((t) => (t.id === slide.id))
                ); // Ensure unique IDs
              });
            }
            setIsLoading(false);
            setCurrentlyResettingKey(null); // Clear reset key
            resolve();
          } catch (parseError) {
            console.error(`Error parsing response for ${bufferKey}:`, parseError);
            if (isResetting) {
              handleError(new Error(`Failed to process reset response for ${dimensionName}. The AI response may be malformed.`), 'reset');
            } else {
              handleError(new Error(`Failed to process AI response for ${dimensionName}. The response format may be invalid.`), 'aiProcessing');
            }
            resolve(); 
          }
          messageBuffer[bufferKey] = ''; 
        } else {
          messageBuffer[bufferKey] += message.text; 
        }
      } catch (error) {
        console.error(`Unexpected error in handleMessage for ${dimensionName}:`, error);
        if (isResetting) {
          handleError(error, 'reset');
        } else {
          handleError(error, 'aiProcessing');
        }
        resolve();
      }
    };
  })();
  
  const handleResetDimension = async (slideIdToReset) => { 
    try {
      if (!currentThreadId) {
        handleError(new Error("No active session found. Please generate slides first."), 'reset');
        return;
      }

      const parts = slideIdToReset.split('-');
      const dimensionName = parts[0];
      const contentKey = parts.slice(1, -1).join('-'); 
      const originalSubtopicNumericalIndex = parts[parts.length -1];

      const originalSlide = slides.find(s => s.id === slideIdToReset);
      if (!originalSlide) {
        handleError(new Error(`Could not find original slide data for ID: ${slideIdToReset}`), 'reset');
        return;
      }

      const actualContentKey = originalSlide.contentKey;

      const subtopicTitleMap = {
        'Objectives-learningObjectives': 'Learning Objectives',
        'Explore-content': 'Main Content',
        'Explore-explanation': 'Explanation',
        'Explore-observations': 'Observations',
        'Explore-fascinatingFacts': 'Fascinating Facts',
        'Compare-analogy': 'Analogy',
        'Compare-content': 'Comparison Content',
        'Compare-explanation': 'Comparison Explanation',
        'Compare-comparison': 'Comparison Table/Details',
        'Question-negation_of_chance': 'Questions on Negation of Chance',
        'Question-negation_of_material_causes': 'Questions on Negation of Material Causes',
        'Question-negation_of_nature': 'Questions on Negation of Nature',
        'Question-conclusion': 'Conclusion for Question Dimension',
        'Connect-interconnectedness': 'Interconnectedness Section',
        'Connect-completeDependency': 'Complete Dependency Section',
        'Connect-allahNames': "Allah's Names Section",
        'Connect-analogicalReflection': 'Analogical Reflection',
        'Connect-questionsForDeeperConnection': 'Questions for Deeper Connection',
        'Connect-contemplationAndAppreciation': 'Contemplation and Appreciation',
        'Appreciate-whatIfs': "'What Ifs' Section",
        'Appreciate-zikrFikrShukr': "'Zikr, Fikr, Shukr' Section",
        'Appreciate-characterLessons': 'Character Lessons',
        'Appreciate-connectWithQuran': 'Connection with Quran',
        'Appreciate-connectWithHadith': 'Connection with Hadith',
        'Appreciate-dua': 'Dua (Supplication) Section',
        'Activities-main': 'Main Activities List'
      };

      const subtopicTitleForPrompt = subtopicTitleMap[`${dimensionName}-${actualContentKey}`] || actualContentKey.replace(/_/g, ' ') || 'the specific section';

      setCurrentlyResettingKey(slideIdToReset); 
      setIsLoading(true); 
      setError('');

      let topicContentForReset = topic;
      if (topicFile) { 
        try { 
          topicContentForReset = await readFileContent(topicFile); 
        } catch(e) { 
          handleError(e, 'fileReading'); 
          return; 
        }
      }

      if (!topicContentForReset.trim() && !topicFile) {
        console.warn("Topic content for reset is based on current input.");
      }

      let standardsContentForReset = standards;
      if (standardsFile) { 
        try { 
          standardsContentForReset = await readFileContent(standardsFile); 
        } catch(e) { 
          handleError(e, 'fileReading'); 
          return; 
        }
      }

      const getOriginalDimensionInput = async (dimName) => {
        try {
          let inputText = ''; let inputFile = null;
          switch (dimName) {
            case 'Objectives': inputText = objectivesInput; inputFile = objectivesFile; break;
            case 'Explore': inputText = exploreInput; inputFile = exploreFile; break;
            case 'Compare': inputText = compareInput; inputFile = compareFile; break;
            case 'Question': inputText = questionInput; inputFile = questionFile; break;
            case 'Connect': inputText = connectInput; inputFile = connectFile; break;
            case 'Appreciate': inputText = appreciateInput; inputFile = appreciateFile; break;
            case 'Activities': inputText = activitiesInput; inputFile = activitiesFile; break;
            default: return 'N/A';
          }
          let content = inputText;
          if (inputFile) { 
            try { 
              const fileContent = await readFileContent(inputFile); 
              content = content ? `${content}\n${fileContent}` : fileContent; 
            } catch (e) { 
              handleError(new Error(`Error reading file for ${dimName}: ${e.message}`), 'fileReading'); 
              throw e; 
            }
          }
          return content || 'N/A';
        } catch (error) {
          handleError(error, 'fileReading');
          throw error;
        }
      };
      
      let originalDimensionOverallInput;
      try { 
        originalDimensionOverallInput = await getOriginalDimensionInput(dimensionName); 
      } catch (e) { 
        return; 
      }

      const dimensionConfig = DIMENSIONS_CONFIG_BASE.find(d => d.name === dimensionName);
      if (!dimensionConfig) { 
        handleError(new Error(`Configuration for ${dimensionName} not found.`), 'reset'); 
        return; 
      }

      const resetPrompt = `Reset and return a new different "${subtopicTitleForPrompt}" section only.`;

      const assistantTitle = isBetaTest ? '5D Thinking-2' : '5D Thinking-1';

      try {
        await fetchContentForDimension(
          currentThreadId,
          { name: dimensionName, title: dimensionConfig.title, input: originalDimensionOverallInput }, 
          topicContentForReset, gradeLevel, standardsContentForReset, assistantTitle,
          true, slideIdToReset, resetPrompt 
        );
      } catch (error) {
        if (error.message?.includes('quota') || error.message?.includes('limit')) {
          handleError(error, 'rateLimited');
        } else if (error.message?.includes('network')) {
          handleError(error, 'network');
        } else {
          handleError(error, 'reset');
        }
      }
    } catch (error) {
      handleError(error, 'reset');
    }
  };

  // Main function to export slides
  const exportSlidesToGoogleSlides = async () => {
    console.log('DEBUG: Exporting slides to Google Slides:', slides);
    if (!slides.length) {
      handleError(new Error('Please generate slides first.'), 'export');
      return;
    }
  
    setIsLoading(true);
    setError('');
  
    try {
      const accessToken = sessionStorage.getItem('googleAuthToken');
      const tokenExpiryTime = sessionStorage.getItem('tokenExpiryTime');
  
      // Check if access token is missing or expired
      if (!accessToken || (tokenExpiryTime && new Date() > new Date(tokenExpiryTime))) {
        handleError(new Error('Access token is invalid or expired. Please authenticate again.'), 'authentication');
        setIsLoginModalOpen(true);
        return;
      }
  
      const maxRetries = 3;
      let attempt = 0;
      let success = false;
      
      while (attempt < maxRetries && !success) {
        try {
          attempt++;
          setIsExporting(true);
          const newPresentationId = await createPresentation(accessToken, 'New 5D Lesson Plan');
          await updatePresentation(newPresentationId, accessToken, slides);
          const presentationLink = `https://docs.google.com/presentation/d/${newPresentationId}/edit`;
          setPresentationLink(presentationLink);
          alert('Google Slides presentation created successfully!');
          success = true;
          setIsExporting(false);
        } catch (error) {
          console.error(`Attempt ${attempt} failed:`, error);
          if (attempt >= maxRetries) {
            if (error.message?.includes('quota') || error.message?.includes('limit')) {
              handleError(error, 'rateLimited');
            } else if (error.message?.includes('network')) {
              handleError(error, 'network');
            } else if (error.message?.includes('403') || error.message?.includes('unauthorized')) {
              handleError(error, 'authentication');
              setIsLoginModalOpen(true);
            } else {
              handleError(new Error('Failed to create Google Slides presentation after multiple attempts.'), 'export');
            }
          }
        }
      }
    } catch (error) {
      console.error('Error during Google Slides export:', error);
      if (error.message?.includes('authentication') || error.message?.includes('unauthorized')) {
        handleError(error, 'authentication');
        setIsLoginModalOpen(true);
      } else {
        handleError(error, 'export');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Function to render the input for the selected dimension
  const renderDimensionInput = () => {
    let inputValue = '';
    let onChangeHandler = () => {};
    let fileValue = null;
    let setFileHandler = () => {};

    switch (selectedDimension) {
      case 'Objectives':
        inputValue = objectivesInput;
        onChangeHandler = handleObjectivesInputChange;
        fileValue = objectivesFile;
        setFileHandler = setObjectivesFile;
        break;
      case 'Explore':
        inputValue = exploreInput;
        onChangeHandler = handleExploreInputChange;
        fileValue = exploreFile;
        setFileHandler = setExploreFile;
        break;
      case 'Compare':
        inputValue = compareInput;
        onChangeHandler = handleCompareInputChange;
        fileValue = compareFile;
        setFileHandler = setCompareFile;
        break;
      case 'Question':
        inputValue = questionInput;
        onChangeHandler = handleQuestionInputChange;
        fileValue = questionFile;
        setFileHandler = setQuestionFile;
        break;
      case 'Connect':
        inputValue = connectInput;
        onChangeHandler = handleConnectInputChange;
        fileValue = connectFile;
        setFileHandler = setConnectFile;
        break;
      case 'Appreciate':
        inputValue = appreciateInput;
        onChangeHandler = handleAppreciateInputChange;
        fileValue = appreciateFile;
        setFileHandler = setAppreciateFile;
        break;
      default:
        break;
    }

    return (
      <div className="input-group">
        <h3> <FaBook/> {selectedDimension} Input (Optional):</h3>
        <div className="input-container">
          <textarea
            value={inputValue}
            onChange={onChangeHandler}
            placeholder={`Provide specific input for the '${selectedDimension}' dimension`}
            rows={4}
          />
          <label htmlFor={`${selectedDimension}FileInput`} className="file-upload-icon">
            <FaPaperclip />
          </label>
          <input
            type="file"
            id={`${selectedDimension}FileInput`}
            onChange={(e) => handleFileChange(e, setFileHandler)}
            accept=".txt,.doc,.docx,.pdf"
            style={{ display: 'none' }}
          />
        </div>
        {/* Display the uploaded file name if a file is selected */}
        {fileValue && (
          <div className="file-name">
            <p>Uploaded file: {fileValue.name}</p>
          </div>
        )}
      </div>
    );
  };
  
  const handleExportToPowerPoint = () => {
    if (slides.length === 0) {
      alert('No slides available to export.');
      return;
    }

    console.log('DEBUG: Exporting slides to PowerPoint:', slides);
    try {
      exportSlidesAsPptx(slides);
      alert('PowerPoint export successful!');
    } catch (error) {
      console.error('Error exporting to PowerPoint:', error);
      alert('Failed to export to PowerPoint. Please try again.');
    } finally {
      setIsExportDropdownOpen(false);
    }
  };

  return (
    <div className="five-d-assistant-container">
      {isLoginModalOpen && (
        <LoginModal
          isOpen={isLoginModalOpen}
          onClose={() => setIsLoginModalOpen(false)}
          onLogin={() => {
            console.log('Login successful, closing modal');
            setIsLoginModalOpen(false);
            setError('');
          }}
        />
      )}
     {/* Add Feedback Modal */}
     <FeedbackModal
  isOpen={isFeedbackModalOpen}
  onClose={() => setIsFeedbackModalOpen(false)}
  assistantModel={assistantModel}
  collectedResponses={collectedResponses}
  onSubmit={handleFeedbackSubmit}
/>


      <header className="five-d-assistant-header">
        <h1 className="five-d-title"> 5D Lesson Planner </h1> 
        <img src={fiveDThinkingImg} alt="5D Lesson Planner" className="five-d-logo" />    
        
        {/* Add feedback button to header */}
        <button 
  className="feedback-button"
  onClick={() => setIsFeedbackModalOpen(true)}
>
  <FaComments style={{ marginRight: '8px' }} />
  Feedback
</button>

      </header>

      <div className="input-section">
        <div className="input-group">
            {/* Beta Toggle in top-right corner */}
    <BetaToggle isBetaTest={isBetaTest} setIsBetaTest={setIsBetaTest} />
          <h3>
            <FaListAlt /> Topic Content (Required):
          </h3>
          <div
            className="input-container"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => handleFileDrop(e, setTopicFile, setTopicPreview)}
          >
            <input
              type="text"
              value={topic}
              onChange={handleTopicChange}
              placeholder="Enter the main topic here"
            />
            <label htmlFor="topicFileInput" className="file-upload-icon">
              <FaPaperclip />
            </label>
            <input
              type="file"
              id="topicFileInput"
              onChange={(e) => handleFileChange(e, setTopicFile, setTopicPreview)}
              accept=".txt,.doc,.docx,.pdf"
              style={{ display: 'none' }}
            />
          </div>
          {topicPreview && (
            <div className="file-preview">
              <h4>Topic Preview:</h4>
              <p>
                {topicPreview.slice(0, 500)}
                {topicPreview.length > 500 && '...'}
              </p>
            </div>
          )}
        </div>

        {/* Grade Level Input (Optional) */}
        <div className="input-group">
          <h3> <FaGraduationCap/> Grade Level (Optional):</h3>
          <input
            type="text"
            value={gradeLevel}
            onChange={handleGradeLevelChange}
            placeholder="Specify the grade level (e.g., 9th Grade, Middle School)"
          />
        </div>

        {/* Standards Input (Optional) */}
        <div className="input-group">
          <h3>
            <FaExclamationCircle /> Standards (Optional):
          </h3>
          <div
            className="input-container"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => handleFileDrop(e, setStandardsFile, setStandardsPreview)}
          >
            <input
              type="text"
              value={standards}
              onChange={handleStandardsChange}
              placeholder="Enter the standards to align with (e.g., CCSS, TEKS, NGSS, or your own curriculum standards)"
            />
            <label htmlFor="standardsFileInput" className="file-upload-icon">
              <FaPaperclip />
            </label>
            <input
              type="file"
              id="standardsFileInput"
              onChange={(e) => handleFileChange(e, setStandardsFile, setStandardsPreview)}
              accept=".txt,.doc,.docx,.pdf"
              style={{ display: 'none' }}
            />
          </div>
          {standardsPreview && (
            <div className="file-preview">
              <h4>Standards Preview:</h4>
              <p>
                {standardsPreview.slice(0, 500)}
                {standardsPreview.length > 500 && '...'}
              </p>
            </div>
          )}
        </div>

        {/* Dimension Selector */}
        <div className="input-group">
          <h4>Select Dimension:</h4>
          <div className="custom-dropdown" ref={dropdownRef}>
            <div
              className="custom-dropdown-selected"
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              style={{ borderColor: `#${dimensionColors[selectedDimension]}` }}
            >
              {selectedDimension}
              <FaChevronDown style={{ marginLeft: '8px' }} />
            </div>
            {isDropdownOpen && (
              <div className="custom-dropdown-options">
                {Object.keys(dimensionColors).map((dimension) => (
                  <div
                    key={dimension}
                    className="custom-dropdown-option"
                    onClick={() => handleDimensionSelect(dimension)}
                    style={{
                      backgroundColor: `#${dimensionColors[dimension]}`,
                      color: '#fff',
                    }}
                  >
                    {dimension}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Input for Selected Dimension */}
        {renderDimensionInput()}

        <button onClick={handleSubmit} disabled={isLoading}>
  {isLoading ? (
    <>
      Generating Slides...
      <FaSpinner className="spinner-small" style={{ marginLeft: '8px' }} />
    </>
  ) : (
    'Generate Slides'
  )}
</button>

        {error && <p className="error-message">{error}</p>}
      </div>

      {slides.length > 0 && (
        <div className="slide-content">
          <h2>Generated Slides</h2>
          {slides.map((slide, index) => (
            <SlideContent 
                key={slide.id || index} 
                slide={slide} 
                onResetDimension={handleResetDimension}
                isResettingThisSlide={currentlyResettingKey === slide.id} 
            />
          ))}

          {/* Export Buttons and Dropdown */}
    <div className="export-dropdown-container">
    <button onClick={toggleExportDropdown} className="export-button">
  <FaDownload style={{ marginRight: '8px' }} /> Export Slides {isExportDropdownOpen ? <FaChevronUp style={{ marginLeft: '8px' }} /> : <FaChevronDown style={{ marginLeft: '8px' }} />}
</button>

      {isExportDropdownOpen && (
        <div className="export-dropdown">
          <button className="export-button" onClick={handleExportToPowerPoint}>
            <FaFilePowerpoint style={{ marginRight: '8px' }} /> Export as PowerPoint
          </button>
          <button className="export-button" onClick={exportSlidesToGoogleSlides}>
            <FaGoogleDrive style={{ marginRight: '8px' }} /> Export to Google Slides
          </button>
          <button className="export-button" onClick={handleExportToGoogleDocs}>
            <FaGoogleDrive style={{ marginRight: '8px' }} /> Export to Google Docs
          </button>
          <button className="export-button" onClick={handleExportToWord}>
            <FaFileWord style={{ marginRight: '8px' }} /> Export to Word
          </button>
        </div>
      )}
    </div>

    {/* Spinner overlay */}
    {isExporting && (
      <div className="export-spinner-overlay">
        <FaSpinner className="spinner" />
      </div>
    )}


          {presentationLink && (
            <div className="presentation-link">
              <p>Your presentation is ready:</p>
              <a href={presentationLink} target="_blank" rel="noopener noreferrer">
                Open Presentation
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FiveDAssistant;
